// MACHINERY V2 (Stroje) — Fleet V2 body: type-grouped rows (M2.3).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.3 + §9 D4. Reference impl:
// design_handoff_machinery_v3/machinery-v2-grouped.jsx (V2Body).
//
// THIS IS A BODY, NOT A PAGE. FleetPage owns the shell (rail, toolbar, strip,
// dialogs, readiness popup, toast) AND the single data hook, and swaps this
// component in for FleetV1Page when `fleetView === "V2"`. That is what
// guarantees ONE telemetry poller: `useFleetTelemetry` is called once, inside
// useFleetPageData, inside FleetPage — never here, and never per row. The file
// keeps the name the ticket assigns it.
//
// SECTIONS ARRIVE PRE-GROUPED. useFleetPageData groups the already-filtered
// base set by the slice's `fleetGroupBy` and hands the SAME map to both views
// (M2.3 ruling (i) — one grouping key, not two), so this component sections
// nothing itself. Consequences, all intended:
//   - switching the dimension never re-fetches;
//   - V1's cards and V2's rows can never section the same fleet differently;
//   - the group health counts come from the same presentation states the rail
//     facets use, so a section header cannot disagree with the rail;
//   - a machine in two lines legitimately appears in both sections, while every
//     COUNT stays de-duplicated (flattenGroups keys on machine id).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

import { Machine } from "../../models/models";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import FleetGroupSection from "./FleetGroupSection";
import { FleetMenuAction } from "./FleetCardMenu";
import { MachineryV2CardState, MachineryV2Telemetry } from "./cardStates";
import { FleetGroups } from "./fleetSelectors";
import { sectionTitle } from "./fleetGroupBy";

export interface FleetV2PageProps {
  /** Pre-grouped by the shell (useFleetPageData.sections). */
  sections: FleetGroups;
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  newMachineId: string | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (name: string) => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetV2Page: React.FC<FleetV2PageProps> = ({
  sections, states, telemetry, readiness, newMachineId, collapsedGroups,
  onToggleGroup, buildActions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
      {Object.entries(sections).map(([key, list]) => (
        <FleetGroupSection
          key={key}
          // Collapse state stays keyed on the RAW group key; only the label is
          // translated (see sectionTitle).
          title={sectionTitle(key, translate)}
          machines={list}
          states={states}
          telemetry={telemetry}
          readiness={readiness}
          newMachineId={newMachineId}
          collapsed={collapsedGroups.has(key)}
          onToggle={() => onToggleGroup(key)}
          buildActions={buildActions}
          onOpenProfile={onOpenProfile}
          onOpenReadiness={onOpenReadiness}
        />
      ))}
    </div>
  );
};

export default FleetV2Page;
